import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod/v4';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  useAdminAddons,
  useCreateAddon,
  useUpdateAddon,
  useDeleteAddon,
} from '@/api/hooks/useAdminAddons';
import { useDashboardSummary } from '@/api/hooks/useAdmin';
import { useAdminSports } from '@/api/hooks/useAdminSports';
import { useAdminUnits } from '@/api/hooks/useAdminUnits';
import { useSupportedLocales } from '@/api/hooks/useSupportedLocales';
import { formatCurrency } from '@/lib/format';
import { pickLocaleValue } from '@/lib/i18n-pick';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Plus, Pencil, Trash2, MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AdminAddon } from '@/api/types';

// PR V.c: tabbed-by-locale entry. nameEn is required (canonical English);
// nameFr / descriptionFr are optional. The submit handler assembles these
// into the per-locale map the backend expects (name: { 'en-CA': ..., 'fr-CA'?: ... }).
// Locales absent from the submission map are preserved (never deleted) by
// the backend so an operator who only edits EN doesn't blank their FR.
const schema = z.object({
  nameEn: z.string().min(1),
  nameFr: z.string().optional(),
  descriptionEn: z.string().optional(),
  descriptionFr: z.string().optional(),
  sportCode: z.string().min(1),
  locationId: z.string().min(1),
  unitId: z.string().optional(),
  rentalPrice: z.coerce.number().positive(),
  quantityTotal: z.coerce.number().int().min(1),
});

type FormValues = z.infer<typeof schema>;

export default function AddonsPage() {
  const { t, i18n } = useTranslation();
  const [locationFilter, setLocationFilter] = useState('all');
  const [sportFilter, setSportFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<AdminAddon | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminAddon | null>(null);

  const { data: dashboardSummary } = useDashboardSummary();
  const locations = dashboardSummary?.locationBreakdown ?? [];
  const { data: tenantSports } = useAdminSports();
  const sports = tenantSports ?? [];

  const { data: addons, isLoading } = useAdminAddons({
    locationId: locationFilter !== 'all' ? locationFilter : undefined,
    sportCode: sportFilter !== 'all' ? sportFilter : undefined,
  });

  const createMutation = useCreateAddon();
  const updateMutation = useUpdateAddon();
  const deleteMutation = useDeleteAddon();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting, dirtyFields },
  } = useForm<FormValues>({
    resolver: zodResolver(schema) as never,
    defaultValues: {
      sportCode: 'GOLF_SIM',
      locationId: locations[0]?.locationId || '',
    },
  });

  const { showFrTab } = useSupportedLocales();
  const [activeLocaleTab, setActiveLocaleTab] = useState<string>('en-CA');

  const sportCode = watch('sportCode');
  const formLocationId = watch('locationId');
  const unitId = watch('unitId');

  const { data: unitsData } = useAdminUnits(undefined, sportCode);

  const openCreate = () => {
    setEditingItem(null);
    setActiveLocaleTab('en-CA');
    reset({ sportCode: 'GOLF_SIM', locationId: locations[0]?.locationId || '' });
    setDialogOpen(true);
  };

  const openEdit = (item: AdminAddon) => {
    setEditingItem(item);
    setActiveLocaleTab('en-CA');
    reset({
      // PR V.c: tabbed-by-locale entry preloads both en-CA + fr-CA values
      // from the admin DTO map. Missing fr-CA renders the FR input
      // empty as an entry prompt.
      nameEn: item.name['en-CA'] ?? '',
      nameFr: item.name['fr-CA'] ?? '',
      descriptionEn: item.description?.['en-CA'] ?? '',
      descriptionFr: item.description?.['fr-CA'] ?? '',
      sportCode: item.sportCode,
      locationId: item.locationId,
      unitId: item.unitId || undefined,
      rentalPrice: item.rentalPrice,
      quantityTotal: item.quantityTotal,
    });
    setDialogOpen(true);
  };

  // PR V.c: assemble the per-locale map shape the backend expects.
  // Only include FR keys when the operator actually typed something
  // there. Locales absent from the submission are preserved by the
  // backend -- never deleted.
  const buildLocalizedField = (en: string | undefined, fr: string | undefined): Record<string, string> => {
    const out: Record<string, string> = { 'en-CA': en ?? '' };
    if (showFrTab && fr && fr.length > 0) out['fr-CA'] = fr;
    return out;
  };

  const onSubmit = async (data: FormValues) => {
    try {
      const payload = {
        name: buildLocalizedField(data.nameEn, data.nameFr),
        description: data.descriptionEn || data.descriptionFr
          ? buildLocalizedField(data.descriptionEn, data.descriptionFr)
          : undefined,
        sportCode: data.sportCode,
        locationId: data.locationId,
        unitId: data.unitId,
        rentalPrice: data.rentalPrice,
        quantityTotal: data.quantityTotal,
      };
      if (editingItem) {
        await updateMutation.mutateAsync({ id: editingItem.id, ...payload });
        toast.success(t('admin.addon_updated'));
      } else {
        await createMutation.mutateAsync(payload);
        toast.success(t('admin.addon_created'));
      }
      setDialogOpen(false);
    } catch (err: unknown) {
      const appErr = err as { message?: string };
      toast.error(appErr.message || t('common.error'));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      toast.success(t('admin.addon_deleted'));
    } catch {
      toast.error(t('common.error'));
    }
    setDeleteTarget(null);
  };

  const locationLabel = locationFilter === 'all'
    ? t('admin.all_locations')
    : locations.find((l) => l.locationId === locationFilter)?.locationName ?? t('admin.all_locations');
  const filterMatchedSport = sports.find((s) => s.code === sportFilter);
  const sportFilterLabel = sportFilter === 'all'
    ? t('admin.all_sports')
    : filterMatchedSport
        ? t(`sport_${filterMatchedSport.code}`, { defaultValue: pickLocaleValue(filterMatchedSport.name, i18n.language) })
        : sportFilter;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t('admin.addons')}</h1>
        <Button size="sm" onClick={openCreate}>
          <Plus className="mr-1.5 h-4 w-4" />
          {t('admin.add_addon')}
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="flex flex-wrap items-center gap-3 p-4">
          {locations.length > 1 && (
            <Select value={locationFilter} onValueChange={(v) => setLocationFilter(v ?? 'all')}>
              <SelectTrigger>
                <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-sm">{locationLabel}</span>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('admin.all_locations')}</SelectItem>
                {locations.map((loc) => (
                  <SelectItem key={loc.locationId} value={loc.locationId}>{loc.locationName}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={sportFilter} onValueChange={(v) => setSportFilter(v ?? 'all')}>
            <SelectTrigger>
              <span className="text-sm">{sportFilterLabel}</span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('admin.all_sports')}</SelectItem>
              {sports.map((s) => (
                <SelectItem key={s.code} value={s.code}>{t(`sport_${s.code}`, { defaultValue: pickLocaleValue(s.name, i18n.language) })}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-6">
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10" />)}
            </div>
          ) : !addons || addons.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">
              {t('admin.no_addons')}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[180px]">{t('admin.col_name')}</TableHead>
                    <TableHead className="min-w-[120px]">{t('admin.col_sport')}</TableHead>
                    <TableHead className="hidden md:table-cell">{t('admin.col_location')}</TableHead>
                    <TableHead className="hidden sm:table-cell min-w-[80px]">{t('admin.col_unit')}</TableHead>
                    <TableHead className="min-w-[80px]">{t('admin.col_price')}</TableHead>
                    <TableHead className="min-w-[60px]">{t('admin.col_quantity')}</TableHead>
                    <TableHead className="min-w-[70px]">{t('admin.col_status')}</TableHead>
                    <TableHead className="min-w-[80px]">{t('admin.col_actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {addons.map((item, i) => (
                    <TableRow key={item.id} className={cn(i % 2 === 0 && 'bg-muted/30')}>
                      <TableCell className="font-medium text-sm">
                        {pickLocaleValue(item.name, i18n.language)}
                        {/* PR V.c: discreet badge for bilingual tenants on
                             addons missing a FR translation. */}
                        {showFrTab && !item.name['fr-CA'] && (
                          <Badge variant="outline" className="ml-2 border-amber-200 bg-amber-50 text-amber-700 text-[10px]">
                            {t('admin.not_yet_translated')}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{t(`sport_${item.sportCode}`, { defaultValue: pickLocaleValue(item.sportName, i18n.language) })}</TableCell>
                      <TableCell className="hidden md:table-cell text-sm text-muted-foreground">{item.locationName}</TableCell>
                      <TableCell className="hidden sm:table-cell text-sm text-muted-foreground">{item.unitName || t('admin.all_units')}</TableCell>
                      <TableCell className="font-mono text-sm">{formatCurrency(item.rentalPrice, 'CAD', i18n.language)}</TableCell>
                      <TableCell className="text-sm text-center">{item.quantityTotal}</TableCell>
                      <TableCell>
                        <Badge variant="secondary" className={`text-xs ${item.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
                          {item.active ? t('admin.online') : t('admin.offline')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEdit(item)}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(item)}>
                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingItem ? t('admin.edit_addon') : t('admin.add_addon')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* PR V.c: tabbed-by-locale entry for the addon name +
                description. EN required; FR tab shown when the tenant
                opts into supportedLocales=['en-CA','fr-CA']. Atomic
                save persists both locales in a single mutation; the
                backend never deletes locales absent from the request
                map. Per-tab dirty-dot indicators on the inactive tab
                trigger prevent accidental save-with-pending-edits. */}
            {showFrTab ? (
              <Tabs value={activeLocaleTab} onValueChange={(v) => setActiveLocaleTab((v as string) ?? 'en-CA')}>
                <TabsList>
                  <TabsTrigger value="en-CA">
                    {t('admin.locale_english')}
                    {dirtyFields.nameEn && activeLocaleTab !== 'en-CA' && (
                      <span className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-[#C2713A]" aria-label={t('admin.locale_unsaved_changes')} />
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="fr-CA">
                    {t('admin.locale_french')}
                    {(dirtyFields.nameFr || dirtyFields.descriptionFr) && activeLocaleTab !== 'fr-CA' && (
                      <span className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-[#C2713A]" aria-label={t('admin.locale_unsaved_changes')} />
                    )}
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="en-CA" className="space-y-3">
                  <div>
                    <Label>{t('admin.col_name')}</Label>
                    <Input {...register('nameEn')} className="mt-1" />
                    {errors.nameEn && <p className="mt-1 text-xs text-destructive">{errors.nameEn.message}</p>}
                  </div>
                </TabsContent>
                <TabsContent value="fr-CA" className="space-y-3">
                  <div>
                    <Label>{t('admin.col_name')}</Label>
                    <Input {...register('nameFr')} placeholder={t('admin.locale_french_placeholder')} className="mt-1" />
                  </div>
                </TabsContent>
              </Tabs>
            ) : (
              <div>
                <Label>{t('admin.col_name')}</Label>
                <Input {...register('nameEn')} className="mt-1" />
                {errors.nameEn && <p className="mt-1 text-xs text-destructive">{errors.nameEn.message}</p>}
              </div>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>{t('admin.col_sport')}</Label>
                <Select value={sportCode} onValueChange={(v) => setValue('sportCode', v ?? '')}>
                  <SelectTrigger className="mt-1">
                    <span className="text-sm">
                      {(() => {
                        const s = sports.find((x) => x.code === sportCode);
                        return s ? t(`sport_${s.code}`, { defaultValue: pickLocaleValue(s.name, i18n.language) }) : sportCode;
                      })()}
                    </span>
                  </SelectTrigger>
                  <SelectContent>
                    {sports.map((s) => (
                      <SelectItem key={s.code} value={s.code}>
                        {t(`sport_${s.code}`, { defaultValue: pickLocaleValue(s.name, i18n.language) })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('admin.col_location')}</Label>
                <Select value={formLocationId || ''} onValueChange={(v) => setValue('locationId', v ?? '')}>
                  <SelectTrigger className="mt-1">
                    <span className="text-sm">{locations.find((l) => l.locationId === formLocationId)?.locationName ?? ''}</span>
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map((loc) => (
                      <SelectItem key={loc.locationId} value={loc.locationId}>{loc.locationName}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* Unit (optional) */}
            {unitsData && unitsData.units.length > 0 && (
              <div>
                <Label>{t('admin.col_unit')}</Label>
                <Select value={unitId || 'all'} onValueChange={(v) => setValue('unitId', (v ?? 'all') === 'all' ? undefined : v!)}>
                  <SelectTrigger className="mt-1">
                    <span className="text-sm">
                      {unitId ? unitsData.units.find((u) => u.id === unitId)?.name ?? unitId : t('admin.all_units')}
                    </span>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('admin.all_units')}</SelectItem>
                    {unitsData.units
                      .filter((u) => !formLocationId || u.locationId === formLocationId)
                      .map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.name} — {u.locationName}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>{t('admin.col_price')}</Label>
                <Input type="number" step="0.01" min="0" {...register('rentalPrice')} className="mt-1" />
                {errors.rentalPrice && <p className="mt-1 text-xs text-destructive">{errors.rentalPrice.message}</p>}
              </div>
              <div>
                <Label>{t('admin.col_quantity')}</Label>
                <Input type="number" min="1" {...register('quantityTotal')} className="mt-1" />
                {errors.quantityTotal && <p className="mt-1 text-xs text-destructive">{errors.quantityTotal.message}</p>}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
              <Button type="submit" disabled={isSubmitting}>
                {editingItem ? t('common.save') : t('admin.add_addon')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('admin.delete_addon')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {t('admin.delete_addon_confirm', { name: deleteTarget?.name })}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>{t('common.cancel')}</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isPending}>
              {t('admin.delete_addon')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
